import { useEffect, useState } from "react";
import { Outlet, Navigate, useNavigate } from "react-router-dom";
import { WifiOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useOnline } from "@/hooks/useFetch";
import { api } from "@/lib/api";
import { Sidebar, BottomTabs } from "./Sidebar";
import { Header } from "./Header";
import { CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { TooltipProvider } from "@/components/ui/tooltip";
import { RecipeStatusBadge } from "@/components/common/Badges";

function SearchCommand({ open, setOpen }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState({ recipes: [], articles: [] });
  const navigate = useNavigate();
  useEffect(() => {
    if (q.trim().length < 2) { setRes({ recipes: [], articles: [] }); return; }
    const t = setTimeout(() => api.get("/search", { params: { q } }).then((r) => setRes(r.data)).catch(() => {}), 200);
    return () => clearTimeout(t);
  }, [q]);
  const go = (path) => { setOpen(false); setQ(""); navigate(path); };
  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Cerca ricette e articoli…" value={q} onValueChange={setQ} data-testid="search-input" />
      <CommandList>
        <CommandEmpty>{q.length < 2 ? "Digita almeno 2 caratteri" : "Nessun risultato"}</CommandEmpty>
        {res.recipes.length > 0 && <CommandGroup heading="Ricette">
          {res.recipes.map((r) => <CommandItem key={r.id} value={`r-${r.name}`} onSelect={() => go(`/ricette/${r.id}`)} data-testid={`search-result-recipe-${r.id}`}>
            <span className="flex-1">{r.name}</span><span className="text-xs text-slate-500">{r.category}</span><RecipeStatusBadge value={r.status} />
          </CommandItem>)}
        </CommandGroup>}
        {res.articles.length > 0 && <CommandGroup heading="Articoli">
          {res.articles.map((a) => <CommandItem key={a.id} value={`a-${a.name}`} onSelect={() => go(`/scorte/${a.id}`)} data-testid={`search-result-article-${a.id}`}>
            <span className="flex-1">{a.name}</span><span className="text-xs text-slate-500 tnum">{a.code} · {a.unit}</span>
          </CommandItem>)}
        </CommandGroup>}
      </CommandList>
    </CommandDialog>
  );
}

export function OfflineBanner({ queued = 0 }) {
  const online = useOnline();
  if (online && !queued) return null;
  return (
    <div className="no-print flex items-center gap-2 bg-[#D97706] px-4 py-2 text-sm font-medium text-white" role="status" data-testid="offline-banner">
      <WifiOff className="h-4 w-4" />
      {online ? `Sincronizzazione di ${queued} operazioni in corso…` : `Sei offline: le operazioni vengono messe in coda${queued ? ` (${queued} in attesa)` : ""} e sincronizzate al ritorno della rete.`}
    </div>
  );
}

export default function AppShell() {
  const { user, siteId, sites } = useAuth();
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    const h = (e) => { if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setSearchOpen((o) => !o); } };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  if (user === null) return <div className="flex min-h-screen items-center justify-center text-sm text-slate-500" data-testid="auth-loading">Caricamento…</div>;
  if (user === false) return <Navigate to="/login" replace />;

  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex min-h-screen">
        <Sidebar />
        <div className="flex min-w-0 flex-1 flex-col">
          <Header onOpenSearch={() => setSearchOpen(true)} />
          <OfflineBanner />
          <main className="flex-1 px-4 pb-24 pt-5 md:px-8 md:pb-10 md:pt-6 print-area">
            {!siteId || sites.length === 0 ? (
              <div className="card p-6 text-sm text-slate-600" data-testid="no-site-state">Nessuna sede assegnata al tuo utente. Chiedi all'amministratore di assegnarti una sede.</div>
            ) : <Outlet key={siteId} />}
          </main>
        </div>
        <BottomTabs />
        <SearchCommand open={searchOpen} setOpen={setSearchOpen} />
      </div>
    </TooltipProvider>
  );
}
